import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Trash2, Lock, Settings, List, Calendar, Gauge, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function ParameterTypesDialog({ open, onOpenChange, onTypesUpdated }) {
  const [types, setTypes] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTypes();
    }
  }, [open]);

  const fetchTypes = async () => {
    try {
      const response = await axios.get(`${API}/parameter-types`, getAuthHeaders());
      setTypes(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки типов параметров');
    }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) {
      toast.error('Введите название типа параметра');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/parameter-types`,
        { name: newTypeName },
        getAuthHeaders()
      );
      toast.success('Тип параметра добавлен');
      setNewTypeName('');
      fetchTypes();
      onTypesUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка добавления типа');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteType = async (typeId, typeName) => {
    if (!window.confirm(`Удалить тип параметра "${typeName}"?`)) return;

    try {
      await axios.delete(`${API}/parameter-types/${typeId}`, getAuthHeaders());
      toast.success('Тип параметра удален');
      fetchTypes();
      onTypesUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка удаления типа');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-x-hidden overflow-y-hidden flex flex-col p-0 bg-white" data-testid="parameter-types-dialog">
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 text-white shadow-2xl sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
              <Settings className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl sm:text-2xl font-bold">Управление типами параметров</DialogTitle>
              <DialogDescription className="text-purple-100 font-medium text-sm mt-1">
                Добавляйте свои типы параметров для отслеживания обслуживания автобусов
              </DialogDescription>
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-10 w-10 p-0 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all flex-shrink-0"
              title="Закрыть"
            >
              <X className="w-5 h-5" strokeWidth={2.5} />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-gradient-to-b from-gray-50 to-white px-4 sm:px-6 py-4 space-y-6">
          {/* Add new type */}
          <div className="bg-white rounded-xl shadow-sm border-2 border-green-100 p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-green-100">
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Добавить новый тип</h3>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 space-y-2">
                <Label htmlFor="new-type" className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <Settings className="w-4 h-4 text-green-600" />
                  Название типа параметра
                </Label>
                <Input
                  id="new-type"
                  data-testid="new-type-input"
                  placeholder="Например: Проверка кондиционера"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddType()}
                  className="h-11 text-sm border-2 border-green-200 focus:border-green-500 transition-colors"
                />
              </div>
              <Button
                onClick={handleAddType}
                disabled={loading}
                data-testid="add-type-button"
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all font-bold h-11 sm:mt-7"
              >
                <Plus className="w-5 h-5 mr-2" strokeWidth={2.5} />
                {loading ? 'Добавление...' : 'Добавить'}
              </Button>
            </div>
          </div>

          {/* Types list */}
          <div className="bg-white rounded-xl shadow-sm border-2 border-purple-100 p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-purple-100">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <List className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Доступные типы параметров</h3>
              <span className="ml-auto bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-bold">
                {types.length}
              </span>
            </div>
            
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {types.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Settings className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="font-medium">Типы параметров не найдены</p>
                  <p className="text-sm mt-1">Добавьте первый тип параметра выше</p>
                </div>
              ) : (
                types.map((type) => (
                  <div
                    key={type.id}
                    className="flex items-center justify-between p-3 sm:p-4 border-2 border-purple-200 rounded-lg bg-gradient-to-r from-white to-purple-50/30 hover:shadow-md transition-all"
                    data-testid={`type-item-${type.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        {type.tracking_type === 'mileage' ? (
                          <Gauge className="w-5 h-5 text-purple-700" strokeWidth={2} />
                        ) : (
                          <Calendar className="w-5 h-5 text-purple-700" strokeWidth={2} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{type.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {type.tracking_type === 'mileage' ? '🚗 По пробегу' : '📅 По дате'}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleDeleteType(type.id, type.name)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-800 hover:bg-red-100 h-10 w-10 p-0 rounded-lg transition-all flex-shrink-0"
                      data-testid={`delete-type-${type.id}`}
                      title="Удалить тип"
                    >
                      <Trash2 className="w-5 h-5" strokeWidth={2} />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}